import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { apiClient } from "@/lib/apiClient";
import { useCompany } from "@/context/CompanyContext";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { toast } from "sonner";
import {
  Building2,
  Plus,
  ArrowUpRight,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  Bell,
  Loader2,
  MoreVertical,
  Archive,
  RotateCcw,
  Eye,
  Inbox,
  FileText,
  ScanLine,
  Landmark,
  ArrowRight,
  ChevronDown,
  ChevronUp,
  Check,
  CheckSquare,
  Send,
  Sparkles,
  Clock,
  EyeOff,
} from "lucide-react";

const fmt = (n, cur = "CHF") =>
  `${cur} ${Number(n || 0)
    .toLocaleString("de-CH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    .replace(/,/g, "'")}`;

function roleBadge(role) {
  const map = {
    owner: { label: "Owner", className: "bg-slate-900 text-white" },
    advisor: { label: "Advisor", className: "bg-emerald-100 text-emerald-800" },
    viewer: { label: "Viewer", className: "bg-slate-100 text-slate-600" },
  };
  const v = map[role] || map.owner;
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold ${v.className}`}>
      {v.label}
    </span>
  );
}

// ---------- Tasks Inbox ----------

const KIND_META = {
  overdue_invoice: { icon: FileText, color: "text-amber-700", bg: "bg-amber-50" },
  dunning_due: { icon: Bell, color: "text-rose-700", bg: "bg-rose-50" },
  receipt_inbox: { icon: ScanLine, color: "text-slate-700", bg: "bg-slate-100" },
  unmatched_bank_tx: { icon: Landmark, color: "text-sky-700", bg: "bg-sky-50" },
};

const PRIORITY_META = {
  high: { label: "Dringend", className: "bg-rose-100 text-rose-800" },
  normal: { label: "Normal", className: "bg-amber-100 text-amber-800" },
  low: { label: "Niedrig", className: "bg-slate-100 text-slate-600" },
};

function TasksInbox({ onOpen, onReload }) {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [highCount, setHighCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [expanded, setExpanded] = useState(false);
  const [selected, setSelected] = useState(new Set()); // task.id Set
  const [running, setRunning] = useState(false);
  const [includeSnoozed, setIncludeSnoozed] = useState(false);

  const fetchTasks = useCallback(() => {
    setLoading(true);
    const qs = includeSnoozed ? "&include_snoozed=true" : "";
    apiClient
      .get(`/my/tasks?limit=50${qs}`)
      .then((res) => {
        setTasks(res.data?.tasks || []);
        setHighCount(res.data?.high_count || 0);
        setTotalCount(res.data?.total_count || 0);
      })
      .catch((e) => console.error(e))
      .finally(() => setLoading(false));
  }, [includeSnoozed]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  const toggleSelect = (taskId, event) => {
    event.stopPropagation();
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(taskId)) next.delete(taskId);
      else next.add(taskId);
      return next;
    });
  };

  const dunningTasks = tasks.filter((t) => t.kind === "dunning_due");
  const allDunningSelected =
    dunningTasks.length > 0 && dunningTasks.every((t) => selected.has(t.id));

  const toggleSelectAllDunning = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allDunningSelected) {
        dunningTasks.forEach((t) => next.delete(t.id));
      } else {
        dunningTasks.forEach((t) => next.add(t.id));
      }
      return next;
    });
  };

  const selectedDunningItems = tasks.filter(
    (t) => t.kind === "dunning_due" && selected.has(t.id)
  );
  const selectedCount = selectedDunningItems.length;

  const runBulkDunning = async () => {
    if (selectedCount === 0) return;
    if (
      !window.confirm(
        `${selectedCount} Mahnung(en) jetzt ausführen?\n\nFür jede Rechnung wird die nächste Mahnstufe ausgelöst und eine E-Mail (sofern SMTP konfiguriert) versendet.`
      )
    ) {
      return;
    }
    setRunning(true);
    try {
      const items = selectedDunningItems.map((t) => ({
        company_id: t.company_id,
        invoice_id: t.id.replace(/^dun-/, ""),
      }));
      const res = await apiClient.post(`/my/tasks/bulk-dunning`, {
        items,
        send_email: true,
      });
      const { success, failed, emails_sent } = res.data || {};
      if (failed > 0) {
        toast.warning(
          `${success}/${success + failed} Mahnungen erfolgreich · ${emails_sent} E-Mail(s) versendet · ${failed} Fehler`
        );
      } else {
        toast.success(
          `${success} Mahnung(en) ausgeführt · ${emails_sent} E-Mail(s) versendet`
        );
      }
      setSelected(new Set());
      fetchTasks();
      if (onReload) onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Batch-Mahnung fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setRunning(false);
    }
  };

  const [singleRunning, setSingleRunning] = useState(null); // task.id currently running single-dun
  const runSingleDunning = async (task, event) => {
    event.stopPropagation();
    if (!window.confirm(`Mahnung für "${task.label}" jetzt ausführen?`)) return;
    setSingleRunning(task.id);
    try {
      const invoiceId = task.id.replace(/^(dun|inv)-/, "");
      const res = await apiClient.post(`/my/tasks/bulk-dunning`, {
        items: [{ company_id: task.company_id, invoice_id: invoiceId }],
        send_email: true,
      });
      const d = res.data?.details?.[0] || {};
      if (d.error) {
        toast.error(`Mahnung fehlgeschlagen: ${d.error}`);
      } else if (d.email_sent) {
        toast.success(`Mahnstufe ${d.level} ausgelöst · E-Mail versendet`);
      } else {
        toast.success(
          `Mahnstufe ${d.level} ausgelöst${d.email_error ? " (E-Mail: " + d.email_error + ")" : ""}`
        );
      }
      fetchTasks();
      if (onReload) onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Mahnung fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setSingleRunning(null);
    }
  };

  const applyBankMatchSuggestion = async (task, event) => {
    event.stopPropagation();
    const entryId = task.id.replace(/^bnk-/, "");
    if (
      !window.confirm(
        `Bank-Transaktion zuordnen zu:\n${task.suggested_match_label}\n\nFortfahren?`
      )
    ) {
      return;
    }
    setSingleRunning(task.id);
    try {
      await apiClient.post(
        `/bank-transactions/${entryId}/apply-match`,
        {
          candidate_type: task.suggested_match_type,
          candidate_id: task.suggested_match_id,
        }
      );
      toast.success(`Zuordnung erfolgreich: ${task.suggested_match_label}`);
      fetchTasks();
      if (onReload) onReload();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Zuordnung fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setSingleRunning(null);
    }
  };

  const snoozeTask = async (task, days, event) => {
    event.stopPropagation();
    setSingleRunning(task.id);
    try {
      await apiClient.post(`/my/tasks/${task.id}/snooze`, { days });
      toast.success(
        days === 365 ? "Task dauerhaft ausgeblendet." : `Task für ${days} Tag(e) ausgeblendet.`
      );
      fetchTasks();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Snooze fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setSingleRunning(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-4 border border-slate-200 bg-white shadow-sm" data-testid="portfolio-tasks-loading">
        <div className="flex items-center gap-2 text-xs text-slate-500">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Lade Aufgaben-Inbox ...
        </div>
      </Card>
    );
  }

  if (totalCount === 0) {
    return null;
  }

  const visible = expanded ? tasks : tasks.slice(0, 5);

  return (
    <Card
      className="border border-slate-200 bg-gradient-to-br from-white to-slate-50/50 shadow-sm"
      data-testid="portfolio-tasks-inbox"
    >
      <div className="p-5 pb-3 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Inbox className="h-4 w-4" />
          </div>
          <div>
            <h2 className="text-sm font-semibold text-slate-900">
              Aufgaben-Inbox
            </h2>
            <p className="text-xs text-slate-500">
              {totalCount} Aufgabe(n) über alle Firmen · {highCount} dringend
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-1.5 text-[11px] text-slate-500 cursor-pointer px-2 py-1 rounded-md hover:bg-slate-100">
            <input
              type="checkbox"
              checked={includeSnoozed}
              onChange={(e) => setIncludeSnoozed(e.target.checked)}
              className="h-3 w-3 rounded border-slate-300"
              data-testid="portfolio-include-snoozed-toggle"
            />
            <EyeOff className="h-3 w-3" />
            Snoozed zeigen
          </label>
          {dunningTasks.length > 0 && (
            <Button
              size="sm"
              variant="outline"
              onClick={toggleSelectAllDunning}
              data-testid="portfolio-tasks-select-all-dunning"
              className="text-xs"
            >
              {allDunningSelected ? "Auswahl aufheben" : `Alle ${dunningTasks.length} Mahnungen wählen`}
            </Button>
          )}
          {tasks.length > 5 && (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setExpanded((v) => !v)}
              data-testid="portfolio-tasks-expand-button"
            >
              {expanded ? (
                <>
                  <ChevronUp className="h-3.5 w-3.5 mr-1" /> Weniger
                </>
              ) : (
                <>
                  <ChevronDown className="h-3.5 w-3.5 mr-1" /> Alle {tasks.length} anzeigen
                </>
              )}
            </Button>
          )}
        </div>
      </div>
      {/* Bulk-Action-Bar */}
      {selectedCount > 0 && (
        <div
          className="mx-2 mb-2 rounded-md border border-slate-900 bg-slate-900 text-white px-4 py-2.5 flex items-center justify-between gap-3"
          data-testid="portfolio-bulk-action-bar"
        >
          <div className="flex items-center gap-2 text-xs">
            <CheckSquare className="h-3.5 w-3.5" />
            <span className="font-medium">
              {selectedCount} Mahnung(en) ausgewählt
            </span>
          </div>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelected(new Set())}
              className="h-7 text-xs text-slate-300 hover:text-white hover:bg-slate-800"
              data-testid="portfolio-bulk-clear-button"
            >
              Löschen
            </Button>
            <Button
              size="sm"
              onClick={runBulkDunning}
              disabled={running}
              className="h-7 bg-emerald-500 hover:bg-emerald-600 text-white text-xs"
              data-testid="portfolio-bulk-run-button"
            >
              {running ? (
                <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              ) : (
                <Send className="h-3.5 w-3.5 mr-1.5" />
              )}
              {running ? "Wird ausgeführt ..." : "Jetzt mahnen"}
            </Button>
          </div>
        </div>
      )}
      <div className="px-2 pb-3 space-y-1">
        {visible.map((t) => {
          const meta = KIND_META[t.kind] || KIND_META.overdue_invoice;
          const Icon = meta.icon;
          const prio = PRIORITY_META[t.priority] || PRIORITY_META.normal;
          const isSelectable = t.kind === "dunning_due";
          const isSelected = selected.has(t.id);
          return (
            <div
              key={t.id}
              className={`w-full flex items-center gap-2 pl-2 pr-3 py-2 rounded-md transition-colors group ${
                isSelected ? "bg-slate-100" : "hover:bg-slate-100"
              }`}
              data-testid={`portfolio-task-${t.id}`}
            >
              {isSelectable ? (
                <button
                  type="button"
                  onClick={(e) => toggleSelect(t.id, e)}
                  className="flex h-4 w-4 items-center justify-center rounded border border-slate-300 bg-white hover:border-slate-500 flex-shrink-0"
                  aria-label={isSelected ? "Abwählen" : "Auswählen"}
                  data-testid={`portfolio-task-checkbox-${t.id}`}
                >
                  {isSelected && <Check className="h-3 w-3 text-slate-900" />}
                </button>
              ) : (
                <div className="w-4 flex-shrink-0" />
              )}
              <button
                type="button"
                onClick={() => onOpen(t)}
                className="flex-1 flex items-center gap-3 text-left min-w-0"
              >
                <div
                  className={`h-7 w-7 rounded-md ${meta.bg} ${meta.color} flex items-center justify-center flex-shrink-0`}
                >
                  <Icon className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 text-xs">
                    <span className="font-medium text-slate-900 truncate">
                      {t.label}
                    </span>
                    <span
                      className={`inline-flex items-center rounded-full px-1.5 py-0 text-[9px] font-semibold ${prio.className}`}
                    >
                      {prio.label}
                    </span>
                  </div>
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">
                    <span className="font-medium text-slate-700">
                      {t.company_name}
                    </span>
                    {t.subtitle ? ` · ${t.subtitle}` : ""}
                  </div>
                  {t.suggested_match_id && (
                    <div className="mt-1 inline-flex items-center gap-1 text-[10px] text-emerald-800 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5">
                      <Sparkles className="h-2.5 w-2.5" />
                      <span className="font-medium">Vorschlag:</span>
                      <span className="truncate max-w-[240px]">
                        {t.suggested_match_label}
                      </span>
                    </div>
                  )}
                </div>
                {t.amount != null && (
                  <div className="text-xs font-semibold text-slate-700 tabular-nums flex-shrink-0">
                    {fmt(t.amount, t.currency)}
                  </div>
                )}
              </button>
              {(t.kind === "dunning_due" || t.kind === "overdue_invoice") && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-[11px] px-2 flex-shrink-0"
                  onClick={(e) => runSingleDunning(t, e)}
                  disabled={singleRunning === t.id}
                  data-testid={`portfolio-task-dun-now-${t.id}`}
                  title="Sofort Mahnung für diese Rechnung ausführen"
                >
                  {singleRunning === t.id ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Send className="h-3 w-3 mr-1" />
                  )}
                  Jetzt mahnen
                </Button>
              )}
              {t.kind === "unmatched_bank_tx" && t.suggested_match_id && (
                <Button
                  size="sm"
                  className="h-7 text-[11px] px-2 flex-shrink-0 bg-emerald-600 hover:bg-emerald-700 text-white"
                  onClick={(e) => applyBankMatchSuggestion(t, e)}
                  disabled={singleRunning === t.id}
                  data-testid={`portfolio-task-apply-match-${t.id}`}
                  title={`Zuordnung anwenden: ${t.suggested_match_label}`}
                >
                  {singleRunning === t.id ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <Check className="h-3 w-3 mr-1" />
                  )}
                  Zuordnen
                </Button>
              )}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 w-7 p-0 flex-shrink-0 text-slate-400 hover:text-slate-700"
                    data-testid={`portfolio-task-snooze-trigger-${t.id}`}
                    title="Task ausblenden"
                    onClick={(e) => e.stopPropagation()}
                  >
                    <Clock className="h-3.5 w-3.5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem
                    onClick={(e) => snoozeTask(t, 1, e)}
                    data-testid={`portfolio-task-snooze-1-${t.id}`}
                  >
                    <Clock className="h-3.5 w-3.5 mr-2" /> 1 Tag ausblenden
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => snoozeTask(t, 7, e)}
                    data-testid={`portfolio-task-snooze-7-${t.id}`}
                  >
                    <Clock className="h-3.5 w-3.5 mr-2" /> 7 Tage ausblenden
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={(e) => snoozeTask(t, 30, e)}
                    data-testid={`portfolio-task-snooze-30-${t.id}`}
                  >
                    <Clock className="h-3.5 w-3.5 mr-2" /> 30 Tage ausblenden
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={(e) => snoozeTask(t, 365, e)}
                    data-testid={`portfolio-task-snooze-forever-${t.id}`}
                    className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                  >
                    <EyeOff className="h-3.5 w-3.5 mr-2" /> Dauerhaft verwerfen
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <ArrowRight className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
            </div>
          );
        })}
      </div>
    </Card>
  );
}

// ---------- Portfolio ----------

export function Portfolio() {
  const navigate = useNavigate();
  const { setCompany } = useCompany();
  const { isSuperAdmin } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await apiClient.get(
        `/my/companies/overview${includeArchived ? "?include_archived=true" : ""}`
      );
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Fehler beim Laden der Firmen-Übersicht.");
    } finally {
      setLoading(false);
    }
  }, [includeArchived]);

  useEffect(() => {
    load();
  }, [load]);

  const openCompany = async (companyId) => {
    try {
      const res = await apiClient.get(`/my/companies/${companyId}`);
      setCompany(res.data);
      localStorage.setItem("demoCompanyId", companyId);
      navigate("/");
    } catch (e) {
      console.error(e);
    }
  };

  const openTask = async (task) => {
    setBusyId(task.id);
    try {
      const res = await apiClient.get(`/my/companies/${task.company_id}`);
      setCompany(res.data);
      localStorage.setItem("demoCompanyId", task.company_id);
      navigate(task.link || "/");
    } catch (e) {
      console.error(e);
    } finally {
      setBusyId(null);
    }
  };

  const archiveCompany = async (companyId, name) => {
    if (!window.confirm(`Firma "${name}" wirklich archivieren?\n\nDaten bleiben erhalten; archivierte Firmen können jederzeit reaktiviert werden.`)) {
      return;
    }
    setBusyId(companyId);
    try {
      await apiClient.post(`/my/companies/${companyId}/archive`);
      toast.success(`"${name}" archiviert.`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Archivieren fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setBusyId(null);
    }
  };

  const unarchiveCompany = async (companyId, name) => {
    setBusyId(companyId);
    try {
      await apiClient.post(`/my/companies/${companyId}/unarchive`);
      toast.success(`"${name}" reaktiviert.`);
      await load();
    } catch (e) {
      const msg = e?.response?.data?.detail || "Reaktivieren fehlgeschlagen.";
      toast.error(String(msg));
    } finally {
      setBusyId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-slate-500 py-10" data-testid="portfolio-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Lade Firmen-Übersicht ...
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800" data-testid="portfolio-error">
        {error}
      </div>
    );
  }

  const companies = data?.companies || [];

  return (
    <div className="space-y-6" data-testid="portfolio-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-semibold tracking-tight text-slate-900">
            Alle Firmen
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            Portfolio-Übersicht aller Firmen, auf die Sie Zugriff haben — aktueller Monat.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer px-3 py-1.5 rounded-md border border-slate-200 bg-white hover:bg-slate-50">
            <input
              type="checkbox"
              checked={includeArchived}
              onChange={(e) => setIncludeArchived(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-slate-300"
              data-testid="portfolio-show-archived-toggle"
            />
            Archivierte anzeigen
          </label>
          {!isSuperAdmin && (
          <Button
            size="sm"
            onClick={() => navigate("/new-company")}
            className="rounded-full bg-slate-900 text-white"
            data-testid="portfolio-new-company-button"
          >
            <Plus className="h-4 w-4 mr-1.5" />
            Neue Firma
          </Button>
          )}
        </div>
      </div>

      {/* Tasks-Inbox (über alle Firmen) */}
      <TasksInbox onOpen={openTask} onReload={load} />

      {/* Summary cards – only if more than one active company */}
      {companies.filter((c) => !c.archived).length > 1 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-testid="portfolio-totals">
          <Card className="p-4 border border-slate-200 bg-white shadow-sm">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-500">
              Einnahmen (alle)
            </div>
            <div className="mt-1 text-2xl font-semibold text-emerald-700">
              {fmt(data.total_income_current_month)}
            </div>
            <div className="mt-1 text-xs text-slate-500">Summe dieses Monats</div>
          </Card>
          <Card className="p-4 border border-slate-200 bg-white shadow-sm">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-500">
              Ausgaben (alle)
            </div>
            <div className="mt-1 text-2xl font-semibold text-rose-700">
              {fmt(data.total_expenses_current_month)}
            </div>
            <div className="mt-1 text-xs text-slate-500">Summe dieses Monats</div>
          </Card>
          <Card className="p-4 border border-slate-200 bg-slate-900 text-white shadow-sm">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-slate-300">
              Gewinn (alle)
            </div>
            <div className="mt-1 text-2xl font-semibold">
              {fmt(data.total_profit_current_month)}
            </div>
            <div className="mt-1 text-xs text-slate-300">Saldo dieses Monats</div>
          </Card>
          <Card className="p-4 border border-amber-200 bg-amber-50 shadow-sm">
            <div className="text-[10px] font-semibold tracking-wider uppercase text-amber-800 flex items-center gap-1">
              <AlertTriangle className="h-3 w-3" /> Überfällig
            </div>
            <div className="mt-1 text-2xl font-semibold text-amber-900">
              {data.total_overdue_invoices_count}
            </div>
            <div className="mt-1 text-xs text-amber-800">
              {fmt(data.total_overdue_invoices_amount)} · {data.total_dunning_due_count} Mahnung(en) fällig
            </div>
          </Card>
        </div>
      )}

      {companies.length === 0 ? (
        <Card className="border border-slate-200 bg-white shadow-sm">
          <EmptyState
            testid="portfolio-empty"
            icon={<Building2 className="h-5 w-5" />}
            title="Noch keine Firmen"
            description="Legen Sie Ihre erste GmbH an, um Buchhaltung, Rechnungen und Löhne automatisch zu verwalten."
            action="Erste Firma anlegen"
            onAction={() => navigate("/new-company")}
          />
        </Card>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="portfolio-grid">
          {companies.map((c) => {
            const isProfit = c.profit_current_month >= 0;
            const cardOpacity = c.archived ? "opacity-60" : "";
            return (
              <Card
                key={c.company_id}
                className={`border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md group ${cardOpacity}`}
                data-testid={`portfolio-card-${c.company_id}`}
              >
                <div className="p-5 space-y-4">
                  {/* Header */}
                  <div className="flex items-start justify-between gap-3">
                    <button
                      type="button"
                      className="flex items-center gap-3 min-w-0 flex-1 text-left"
                      onClick={() => !c.archived && openCompany(c.company_id)}
                      disabled={c.archived}
                      data-testid={`portfolio-open-${c.company_id}`}
                    >
                      <div className="h-10 w-10 shrink-0 rounded-lg bg-slate-900 text-white flex items-center justify-center font-semibold text-sm">
                        {c.name.slice(0, 2).toUpperCase()}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h3 className="font-semibold text-slate-900 truncate" title={c.name}>
                            {c.name}
                          </h3>
                          {roleBadge(c.role)}
                          {c.archived && (
                            <Badge variant="outline" className="border-slate-300 bg-slate-100 text-slate-600 text-[10px]">
                              Archiviert
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 mt-0.5">
                          {c.currency} · Aktueller Monat
                        </div>
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0">
                      {!c.archived && (
                        <ArrowUpRight className="h-4 w-4 text-slate-400 transition-transform group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                      )}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="h-7 w-7 p-0"
                            data-testid={`portfolio-menu-trigger-${c.company_id}`}
                            disabled={busyId === c.company_id}
                          >
                            <MoreVertical className="h-3.5 w-3.5" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => openCompany(c.company_id)}
                            data-testid={`portfolio-menu-open-${c.company_id}`}
                          >
                            <Eye className="h-3.5 w-3.5 mr-2" /> Öffnen
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {c.archived ? (
                            <DropdownMenuItem
                              onClick={() => unarchiveCompany(c.company_id, c.name)}
                              data-testid={`portfolio-menu-unarchive-${c.company_id}`}
                              disabled={c.role !== "owner"}
                            >
                              <RotateCcw className="h-3.5 w-3.5 mr-2" /> Reaktivieren
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              onClick={() => archiveCompany(c.company_id, c.name)}
                              data-testid={`portfolio-menu-archive-${c.company_id}`}
                              disabled={c.role !== "owner"}
                              className="text-rose-600 focus:text-rose-700 focus:bg-rose-50"
                            >
                              <Archive className="h-3.5 w-3.5 mr-2" /> Archivieren
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {/* KPIs */}
                  <div className="grid grid-cols-3 gap-2 text-xs">
                    <div className="rounded-md border border-emerald-100 bg-emerald-50/60 p-2.5">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-emerald-700">
                        <TrendingUp className="h-3 w-3" /> Einnahmen
                      </div>
                      <div className="mt-1 font-semibold text-slate-900 tabular-nums">
                        {fmt(c.income_current_month, c.currency)}
                      </div>
                    </div>
                    <div className="rounded-md border border-rose-100 bg-rose-50/60 p-2.5">
                      <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-rose-700">
                        <TrendingDown className="h-3 w-3" /> Ausgaben
                      </div>
                      <div className="mt-1 font-semibold text-slate-900 tabular-nums">
                        {fmt(c.expenses_current_month, c.currency)}
                      </div>
                    </div>
                    <div
                      className={`rounded-md border p-2.5 ${
                        isProfit
                          ? "border-slate-200 bg-slate-50"
                          : "border-amber-200 bg-amber-50/60"
                      }`}
                    >
                      <div className="text-[10px] uppercase tracking-wider text-slate-600">
                        Gewinn
                      </div>
                      <div
                        className={`mt-1 font-semibold tabular-nums ${
                          isProfit ? "text-slate-900" : "text-amber-800"
                        }`}
                      >
                        {fmt(c.profit_current_month, c.currency)}
                      </div>
                    </div>
                  </div>

                  {/* Action items */}
                  <div className="flex flex-wrap items-center gap-2 pt-1 border-t border-slate-100">
                    {c.overdue_invoices_count > 0 ? (
                      <HoverCard openDelay={150} closeDelay={100}>
                        <HoverCardTrigger asChild>
                          <Badge
                            variant="outline"
                            className="border-amber-300 bg-amber-50 text-amber-900 text-[10px] cursor-help"
                            data-testid={`portfolio-overdue-${c.company_id}`}
                          >
                            <AlertTriangle className="h-3 w-3 mr-1" />
                            {c.overdue_invoices_count} überfällig · {fmt(c.overdue_invoices_amount, c.currency)}
                          </Badge>
                        </HoverCardTrigger>
                        <HoverCardContent className="w-80" align="start">
                          <div className="space-y-3">
                            <div>
                              <div className="text-xs font-semibold text-slate-900">
                                Top-{c.top_overdue_invoices?.length || 0} überfällige Rechnungen
                              </div>
                              <div className="text-[11px] text-slate-500">
                                Sortiert nach Tagen überfällig
                              </div>
                            </div>
                            <div className="space-y-2">
                              {(c.top_overdue_invoices || []).map((inv) => (
                                <button
                                  type="button"
                                  key={inv.invoice_id}
                                  onClick={() => {
                                    openCompany(c.company_id).then(() =>
                                      navigate(`/invoices?highlight=${inv.invoice_id}`)
                                    );
                                  }}
                                  className="w-full flex items-center justify-between gap-3 p-2 rounded-md hover:bg-slate-50 text-left"
                                  data-testid={`portfolio-overdue-item-${inv.invoice_id}`}
                                >
                                  <div className="min-w-0 flex-1">
                                    <div className="text-xs font-medium text-slate-900 truncate">
                                      {inv.invoice_number || "–"}
                                    </div>
                                    <div className="text-[10px] text-slate-500 truncate">
                                      {inv.customer_name || "Unbekannt"} · {inv.days_overdue} Tage
                                    </div>
                                  </div>
                                  <div className="text-xs font-semibold text-rose-700 tabular-nums">
                                    {fmt(inv.total, c.currency)}
                                  </div>
                                </button>
                              ))}
                            </div>
                          </div>
                        </HoverCardContent>
                      </HoverCard>
                    ) : null}
                    {c.dunning_due_count > 0 && (
                      <Badge
                        variant="outline"
                        className="border-rose-300 bg-rose-50 text-rose-900 text-[10px]"
                      >
                        <Bell className="h-3 w-3 mr-1" />
                        {c.dunning_due_count} Mahnung(en) fällig
                      </Badge>
                    )}
                    {c.open_invoices_count > 0 && (
                      <span className="text-[11px] text-slate-500">
                        {c.open_invoices_count} offene Rechnung(en)
                      </span>
                    )}
                    {c.open_bills_count > 0 && (
                      <span className="text-[11px] text-slate-500">
                        · {c.open_bills_count} offene Eingangsrechnung(en)
                      </span>
                    )}
                    {c.open_receipts_count > 0 && (
                      <span className="text-[11px] text-slate-500">
                        · {c.open_receipts_count} Beleg(e) in der Inbox
                      </span>
                    )}
                    {c.overdue_invoices_count === 0 &&
                      c.dunning_due_count === 0 &&
                      c.open_invoices_count === 0 &&
                      c.open_bills_count === 0 &&
                      c.open_receipts_count === 0 && (
                        <span className="text-[11px] text-emerald-700 font-medium">
                          ✓ Keine offenen Aufgaben
                        </span>
                      )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default Portfolio;
